import { api } from "./api";
import type {
  User,
  ChangeUserNamePayload,
  UpdatePhonePayload,
  Token,
  ChangeEmailPayload,
  ChangeEmailTokenPayload,
  ChangePasswordPayload,
  InvoicingData,
  MarketingData,
  KycResponse,
  OnmetaKycResponse,
  USDKycResponse,
} from "../../types/UserTypes";

export const accountsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getUser: builder.query<User, void>({
      query: () => ({
        url: "/accounts/me/",
      }),
      providesTags: (res) => [{ type: "Users", id: res?.email }],
    }),
    updatePhone: builder.mutation<null, UpdatePhonePayload>({
      query: (payload) => ({
        url: "/accounts/update_phone/",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Users"],
    }),
    confirmPhone: builder.mutation<null, Token>({
      query: (payload) => ({
        url: "/accounts/confirm_phone/",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Users"],
    }),
    changeEmail: builder.mutation<null, ChangeEmailPayload>({
      query: ({ code, ...rest }) => ({
        url: "/accounts/email-changes/",
        method: "POST",
        body: rest,
        headers: { "X-Archway-2fa": code },
      }),
      invalidatesTags: ["Users"],
    }),
    changeEmailGetToken: builder.query<ChangeEmailTokenPayload, Token>({
      query: ({ token = "" }) => ({
        url: `/accounts/email-changes/${token}/`,
      }),
    }),
    changeEmailConfirm: builder.mutation<any, Token>({
      query: ({ token = "" }) => ({
        url: `/accounts/email-changes/${token}/confirm/`,
        method: "POST",
      }),
    }),
    changePassword: builder.mutation<null, ChangePasswordPayload>({
      query: ({ code, ...rest }) => ({
        url: "/accounts/set_password/",
        method: "POST",
        body: rest,
        headers: { "X-Archway-2fa": code },
      }),
      invalidatesTags: ["Users"],
    }),
    changeDefaultInvoiceData: builder.mutation<null, InvoicingData>({
      query: (payload) => ({
        url: "/accounts/me/",
        method: "PATCH",
        body: { invoicing_data: payload },
      }),
      invalidatesTags: ["Users"],
    }),
    changeUserName: builder.mutation<null, ChangeUserNamePayload>({
      query: (payload) => ({
        url: "/accounts/me/",
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["Users"],
    }),
    changeMarketingNotification: builder.mutation<null, MarketingData>({
      query: (payload) => ({
        url: "/accounts/me/",
        method: "PATCH",
        body: { marketing_data: payload },
      }),
      invalidatesTags: ["Users"],
    }),
    getKycUrl: builder.mutation<KycResponse, void>({
      query: () => ({
        url: `/accounts/start_verification/`,
        method: "POST",
        body: {},
      }),
    }),
    getOnmetaKycUrl: builder.mutation<OnmetaKycResponse, void>({
      query: () => ({
        url: `/accounts/start_onmeta_verification/`,
        method: "POST",
        body: {},
      }),
    }),
    getUSDKycUrl: builder.mutation<USDKycResponse, void>({
      query: () => ({
        url: `/accounts/start_usd_verification/`,
        method: "POST",
        body: {},
      }),
    }),
  }),
});

export const {
  useGetUserQuery,
  useUpdatePhoneMutation,
  useConfirmPhoneMutation,
  useChangeEmailConfirmMutation,
  useChangeEmailGetTokenQuery,
  useChangeEmailMutation,
  useChangePasswordMutation,
  useChangeDefaultInvoiceDataMutation,
  useChangeMarketingNotificationMutation,
  useChangeUserNameMutation,
  useGetKycUrlMutation,
  useGetOnmetaKycUrlMutation,
  useGetUSDKycUrlMutation,
} = accountsApi;
